import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import PageHeader from '../../components/common/PageHeader';
import StatCard from '../../components/common/StatCard';
import DataTable from '../../components/common/DataTable';
import StatusBadge from '../../components/common/StatusBadge';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { formatDateTime, formatDate, toAdelaideDateString } from '../../lib/utils';
import { 
  Users, Home, Calendar, AlertTriangle, Clock, 
  FileText, TrendingUp, RefreshCw, Plus, Play, Square, MapPin, DollarSign, Car
} from 'lucide-react';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import { useNavigate } from 'react-router-dom';

const AdminDashboard = () => {
  const { api, user } = useAuth();
  const basePath = user?.role === 'house_manager' ? '/manager' : '/admin';
  const navigate = useNavigate();
  const [stats, setStats] = useState({});
  const [recentIncidents, setRecentIncidents] = useState([]);
  const [todaysShifts, setTodaysShifts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [recentExpenses, setRecentExpenses] = useState([]);

  // Clock in/out state for managers with shifts
  const [eligibleShifts, setEligibleShifts] = useState([]);
  const [activeClock, setActiveClock] = useState(null);
  const [clockDialogOpen, setClockDialogOpen] = useState(false);
  const [clockOutNotes, setClockOutNotes] = useState("");
  const [clockLoading, setClockLoading] = useState(false);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    setLoading(true);
    try {
      const [statsRes, incidentsRes, shiftsRes] = await Promise.all([
        api.get('/dashboard/stats'),
        api.get('/incidents?status=submitted'),
        api.get('/shifts')
      ]);

      setStats(statsRes.data);
      setRecentIncidents(incidentsRes.data.slice(0, 5));

      // Fetch recent shift notes that have expenses
      try {
        const notesRes = await api.get('/shift-notes');
        const withExpenses = notesRes.data
          .filter(n => n.expenses && n.expenses.length > 0)
          .slice(0, 10);
        setRecentExpenses(withExpenses);
      } catch(e) {}
      
      const today = toAdelaideDateString(new Date());
      const todayShifts = shiftsRes.data.filter(s => s.start_time.startsWith(today));
      setTodaysShifts(todayShifts.slice(0, 5));

      // Fetch clock data for house managers
      if (user?.role === "house_manager") {
        try {
          const [activeRes, eligibleRes] = await Promise.all([
            api.get("/time-clock/active"),
            api.get("/time-clock/eligible-shifts")
          ]);
          setActiveClock(activeRes.data?.active ? activeRes.data.record : null);
          setEligibleShifts(eligibleRes.data || []);
        } catch (e) { /* Clock endpoints might fail */ }
      }
    } catch (error) {
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const handleClockIn = async (shift) => {
    setClockLoading(true);
    try {
      await api.post("/time-clock/clock-in", { shift_id: shift.id });
      toast.success("Clocked in successfully!");
      fetchDashboardData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to clock in");
    } finally {
      setClockLoading(false);
    }
  };

  const handleClockOut = async () => {
    if (!activeClock) return;
    setClockLoading(true);
    try {
      await api.post("/time-clock/clock-out", {
        shift_id: activeClock.shift_id,
        notes: clockOutNotes
      });
      toast.success("Clocked out successfully!");
      setClockDialogOpen(false);
      setClockOutNotes("");
      fetchDashboardData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to clock out");
    } finally {
      setClockLoading(false);
    }
  };

  const incidentColumns = [
    { 
      header: 'Participant', 
      accessor: 'participant_name',
      cell: (row) => row.participant_name || 'Unknown'
    },
    { header: 'Category', accessor: 'category' },
    { 
      header: 'Severity', 
      accessor: 'severity',
      cell: (row) => (
        <span className={`font-medium ${
          row.severity === 'High' ? 'text-red-500' : 
          row.severity === 'Medium' ? 'text-amber-500' : 'text-green-500'
        }`}>
          {row.severity}
        </span>
      )
    },
    { header: 'Status', cell: (row) => <StatusBadge status={row.status} /> },
    { header: 'Date', cell: (row) => formatDate(row.created_at) }
  ];

  const shiftColumns = [
    { header: 'House', accessor: 'house_name' },
    { header: 'Worker', accessor: 'worker_name' },
    { header: 'Type', accessor: 'shift_type' },
    { 
      header: 'Time', 
      cell: (row) => `${formatDateTime(row.start_time).split(' ')[1]} - ${formatDateTime(row.end_time).split(' ')[1]}`
    },
    { header: 'Status', cell: (row) => <StatusBadge status={row.status} /> }
  ];

  return (
    <div className="space-y-8 animate-fade-in" data-testid="admin-dashboard">
      <PageHeader 
        title={user?.role === 'house_manager' ? 'Manager Dashboard' : 'Admin Dashboard'}
        description="Overview of your NDIS support services"
        actions={
          <Button onClick={fetchDashboardData} variant="outline" size="sm">
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh
          </Button>
        }
      />

      {/* Manager Time Clock Section */}
      {user?.role === "house_manager" && activeClock && (
        <Card className="border-2 border-green-500/50 bg-green-500/5">
          <CardContent className="py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-full bg-green-500/20 animate-pulse">
                  <Play className="h-5 w-5 text-green-500" />
                </div>
                <div>
                  <p className="font-semibold text-green-600">Currently Clocked In</p>
                  <p className="text-sm text-muted-foreground">
                    {activeClock.house_name} - Since {formatDateTime(activeClock.clock_in_time)}
                  </p>
                </div>
              </div>
              <Button variant="destructive" size="sm" onClick={() => setClockDialogOpen(true)}>
                <Square className="h-4 w-4 mr-2" />
                Clock Out
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {user?.role === "house_manager" && !activeClock && eligibleShifts.length > 0 && (
        <Card className="border-border/40">
          <CardHeader className="pb-3">
            <CardTitle className="text-lg flex items-center gap-2">
              <Clock className="h-5 w-5" />
              Available Shifts to Clock In
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {eligibleShifts.map(shift => (
                <div key={shift.id} className="flex items-center justify-between p-3 rounded-lg border border-border/40 hover:bg-muted/30">
                  <div className="flex items-center gap-3">
                    <MapPin className="h-4 w-4 text-muted-foreground" />
                    <div>
                      <p className="font-medium">{shift.house_name}</p>
                      <p className="text-sm text-muted-foreground">
                        {formatDateTime(shift.start_time)} - {formatDateTime(shift.end_time).split(' ')[1]}
                      </p>
                    </div>
                  </div>
                  <Button size="sm" onClick={() => handleClockIn(shift)} disabled={clockLoading}>
                    <Play className="h-4 w-4 mr-2" />
                    Clock In
                  </Button>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Stats Grid */}
      <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard title="Total Houses" value={stats.total_houses || 0} icon={Home} />
        <StatCard title="Participants" value={stats.total_participants || 0} icon={Users} />
        <StatCard title="Staff Members" value={stats.total_staff || 0} icon={Calendar} />
        <StatCard title="Pending Incidents" value={stats.pending_incidents || 0} icon={AlertTriangle} />
      </div>

      {/* Quick Stats Row */}
      <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
        <StatCard title="Today's Shifts" value={stats.todays_shifts || 0} icon={Clock} />
        <StatCard title="Pending Leave" value={stats.pending_leave || 0} icon={FileText} />
      </div>

      {/* Data Tables */}
      <div className="grid gap-6 lg:grid-cols-2">
        {/* Recent Incidents */}
        <Card className="border-border/40 bg-card/50 backdrop-blur-sm">
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="text-lg font-semibold">Recent Incidents</CardTitle>
            <Button 
              variant="ghost" 
              size="sm"
              onClick={() => navigate(basePath + '/incidents')}
              data-testid="view-all-incidents"
            >
              View All
            </Button>
          </CardHeader>
          <CardContent>
            <DataTable 
              columns={incidentColumns}
              data={recentIncidents}
              onRowClick={() => navigate(basePath + '/incidents')}
              emptyMessage="No pending incidents"
            />
          </CardContent>
        </Card>

        {/* Today's Shifts */}
        <Card className="border-border/40 bg-card/50 backdrop-blur-sm">
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="text-lg font-semibold">Today's Shifts</CardTitle>
            <Button 
              variant="ghost" 
              size="sm"
              onClick={() => navigate(basePath + '/roster')}
              data-testid="view-roster"
            >
              View Roster
            </Button>
          </CardHeader>
          <CardContent>
            <DataTable 
              columns={shiftColumns}
              data={todaysShifts}
              onRowClick={() => navigate(basePath + '/roster')}
              emptyMessage="No shifts scheduled today"
            />
          </CardContent>
        </Card>
      </div>

      {/* Recent Expenses from Shift Notes */}
      {recentExpenses.length > 0 && (
        <Card className="border-border/40 bg-card/50 backdrop-blur-sm">
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="text-lg font-semibold flex items-center gap-2">
              <DollarSign className="h-5 w-5 text-green-500" />
              Recent Shift Note Expenses
            </CardTitle>
            <Button variant="ghost" size="sm" onClick={() => navigate(basePath + '/notes')}>View All Notes</Button>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {recentExpenses.map(note => {
                const total = note.expenses.reduce((s, e) => s + parseFloat(e.amount || 0), 0);
                return (
                  <div key={note.id} className="flex items-center justify-between p-3 rounded-lg border border-border/40">
                    <div>
                      <p className="font-medium text-sm">{note.worker_name || 'Worker'}</p>
                      <p className="text-xs text-muted-foreground">{note.participant_name} - {formatDate(note.created_at)}</p>
                      <div className="flex gap-2 mt-1">
                        {note.expenses.map((exp, i) => (
                          <span key={i} className="text-xs bg-muted px-2 py-0.5 rounded">{exp.description}: ${parseFloat(exp.amount).toFixed(2)}</span>
                        ))}
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="font-bold text-green-600">${total.toFixed(2)}</p>
                      {note.travel?.kilometers && (
                        <p className="text-xs text-muted-foreground flex items-center gap-1"><Car className="h-3 w-3" />{note.travel.kilometers} km</p>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Clock Out Dialog */}
      <Dialog open={clockDialogOpen} onOpenChange={setClockDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Clock Out</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Shift Notes (optional)</Label>
              <Textarea
                value={clockOutNotes}
                onChange={(e) => setClockOutNotes(e.target.value)}
                placeholder="Any notes about this shift..."
                rows={3}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setClockDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleClockOut} disabled={clockLoading} variant="destructive">
              <Square className="h-4 w-4 mr-2" />
              Clock Out
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminDashboard;
